import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface GanttSkeletonProps {
  className?: string;
}

/**
 * Gantt workspace loading skeleton.
 *
 * Mirrors the Phase 3 shell: a tight toolbar, a left task list, and a right
 * timeline grid. Used as the initial app loading state so the layout shape
 * stays stable while real data is fetched.
 */
export function GanttSkeleton({ className }: GanttSkeletonProps) {
  return (
    <div className={cn('flex h-full flex-col bg-background', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-2">
        <Skeleton className="h-8 w-32 rounded-sm" />
        <div className="flex-1" />
        <Skeleton className="h-8 w-24 rounded-sm" />
        <Skeleton className="h-8 w-8 rounded-sm" />
        <Skeleton className="h-8 w-8 rounded-sm" />
        <Skeleton className="h-8 w-8 rounded-sm" />
      </div>

      {/* Workspace */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Task list */}
        <div className="flex w-64 flex-col border-r border-border">
          <Skeleton className="h-8 w-full rounded-none" />
          <div className="flex-1 space-y-1 p-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 rounded-sm bg-muted/30 p-2">
                <Skeleton className="h-3 w-3 rounded-sm" />
                <Skeleton className="h-3.5 w-3/4 rounded-sm" />
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="flex flex-1 flex-col">
          <Skeleton className="h-8 w-full rounded-none" />
          <div className="flex flex-1 items-end justify-center gap-3 px-6 pb-8">
            <Skeleton className="h-1/2 w-24 rounded-sm" />
            <Skeleton className="h-1/3 w-32 rounded-sm" />
            <Skeleton className="h-2/3 w-20 rounded-sm" />
            <Skeleton className="h-1/4 w-28 rounded-sm" />
          </div>
        </div>
      </div>
    </div>
  );
}
